import React, { useMemo, useState } from 'react';
import { Select, Empty } from 'antd';
import { isEmpty, mapValues, groupBy, omit, startCase } from 'lodash';
import moment from 'moment';

import DualAxisAreaChart from '../../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../../shared/globalStyling/styledText';

// =============================================================================
// BasketItemsBarChart — V1 trend chart, migrated off BizCharts to the shared
// v2 DualAxisAreaChart primitive.
//
// Data shape unchanged: V1 feeds in rows with UPPERCASE keys (PRICE, QUANTITY,
// DATE, TIMESTAMP). We aggregate by date the same way the BizCharts version
// did — sum(quantity), sum(revenue) per calendar day — then normalize the
// output to the lowercase { xAxis, quantity, revenue } shape DualAxisAreaChart
// expects (its `labelToKey` resolves "Quantity" -> "quantity", etc.).
//
// Matches the V2 BasketItemsChart shape: "Trend" eyebrow + inline L/R axis
// selectors in the head, Recharts body below. Default axes Quantity / Revenue.
// =============================================================================

const AXIS_OPTIONS = ['Quantity', 'Revenue'];

const aggregateByDate = (data) => {
  if (isEmpty(data)) return [];

  const grouped = mapValues(groupBy(data, 'date'), (list) =>
    list.map((obj) => omit(obj, 'date'))
  );

  const res = [];
  for (const date in grouped) {
    let quantity = 0;
    let revenue = 0;
    grouped[date].forEach((obj) => {
      quantity += obj.quantity;
      revenue += obj.revenue * obj.quantity;
    });
    res.push({ xAxis: date, quantity, revenue });
  }
  return res.sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
};

const BasketItemsBarChart = ({ data }) => {
  const [leftY, setLeftY] = useState('Quantity');
  const [rightY, setRightY] = useState('Revenue');

  const rows = useMemo(() => {
    if (isEmpty(data)) return [];
    // V1 rows carry PRICE + QUANTITY + TIMESTAMP/DATE. Same pre-aggregation
    // as the legacy implementation: flatten to lowercase { date, quantity,
    // revenue } triples, then aggregate by calendar day.
    const flattened = data.map((obj) => {
      const date = obj.TIMESTAMP ? obj.TIMESTAMP : obj.DATE;
      return {
        quantity: obj.QUANTITY,
        revenue: obj.PRICE,
        date: moment(date).format('YYYY-MM-DD'),
      };
    });
    return aggregateByDate(flattened);
  }, [data]);

  if (isEmpty(rows)) {
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  return (
    <div className="campaign-overview__chart">
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Trend
        </div>

        <div className="campaign-overview__axis-row">
          <div className="campaign-overview__axis-field">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
              Left axis
            </span>
            <Select
              onSelect={(value) => setLeftY(value)}
              className="campaign-overview__axis-select"
              value={leftY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== rightY).map((option) => (
                <Select.Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#7dafff'} />
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
            <span className="campaign-overview__axis-label mono-eyebrow">
              <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
              Right axis
            </span>
            <Select
              onSelect={(value) => setRightY(value)}
              className="campaign-overview__axis-select"
              value={rightY}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
            >
              {AXIS_OPTIONS.filter((op) => op !== leftY).map((option) => (
                <Select.Option key={option} value={option}>
                  <MenuItemTextColor text={startCase(option)} color={'#92d5a7'} />
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="campaign-overview__chart-body">
        <DualAxisAreaChart dualChartData={rows} leftY={leftY} rightY={rightY} />
      </div>
    </div>
  );
};

export default BasketItemsBarChart;
